'use client';

// No captcha by design: spam is handled by honeypot + min-fill-time + IP rate limit.
import { useRef, useState } from 'react';
import Link from 'next/link';
import Turnstile from '@/components/ui/Turnstile';

const CONTACT_INFO = [
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    label: 'Office',
    value: '440 N Wolfe Rd, Sunnyvale, CA 94085',
    href: 'https://maps.google.com/?q=440+N+Wolfe+Rd+Sunnyvale+CA+94085',
  },
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    label: 'Phone',
    value: '425.985.8004',
    href: 'tel:+14259858004',
  },
  {
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    label: 'Email',
    value: 'contact@cyberarmy.ai',
    href: 'mailto:contact@cyberarmy.ai',
  },
  {
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    label: 'LinkedIn',
    value: 'linkedin.com/company/cyber-army',
    href: 'https://www.linkedin.com/company/cyber-army/',
  },
];

const LEAD_LABELS: Record<string, { title: string; subtitle: string; placeholder: string }> = {
  autofix_early_access: {
    title: 'Request AutoFix Early Access',
    subtitle: 'Join our CISO design partner program. We respond within 1 business day.',
    placeholder: 'Tell us about your infrastructure, team size, and what security challenges you are facing...',
  },
  autoscan_free_pentest: {
    title: 'Request a Free AI Pentest Report',
    subtitle: 'Hundreds of AI agents pentest your external attack surface and deliver an auditor-ready report. We respond within 1 business day.',
    placeholder: 'Tell us about your domain, compliance requirements, and current security testing cadence...',
  },
  // legacy alias: old links and bookmarks still resolve to the same form
  autoscan_early_access: {
    title: 'Request a Free AI Pentest Report',
    subtitle: 'Hundreds of AI agents pentest your external attack surface and deliver an auditor-ready report. We respond within 1 business day.',
    placeholder: 'Tell us about your domain, compliance requirements, and current security testing cadence...',
  },
  early_access: {
    title: 'Get Early Access',
    subtitle: 'Join the waitlist. We respond within 1 business day.',
    placeholder: 'Tell us about your security needs and what you are looking for...',
  },
  cybercrawler_disclosure: {
    title: 'Cyber Crawler Disclosure',
    subtitle: 'Verify a disclosure from Cyber Crawler, or ask us about a finding on your assets. We respond within 1 business day.',
    placeholder: 'Reference the asset or domain in question and what you would like to confirm...',
  },
  demo_access: {
    title: 'Request a Live Demo',
    subtitle: 'See the full AutoScan and AutoFix product walkthrough with our team. We respond within 1 business day.',
    placeholder: 'Tell us your company, your role, and what you would like to see in the demo...',
  },
};

// Legacy aliases (pre-AutoScan rename): old ?lead_type=swarm_* links still resolve.
LEAD_LABELS.swarm_free_pentest = LEAD_LABELS.autoscan_free_pentest;
LEAD_LABELS.swarm_early_access = LEAD_LABELS.autoscan_early_access;
// Gated full-demo CTAs route here (and the ?lead_type=demo alias).
LEAD_LABELS.demo = LEAD_LABELS.demo_access;

export default function ContactClient({ searchParams, siteKey }: { searchParams: Record<string, string>; siteKey: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const startedAt = useRef<number>(Date.now());

  const leadType = (searchParams?.lead_type || 'general').toLowerCase();
  const source = searchParams?.source || '';
  const utm_source = searchParams?.utm_source || '';
  const utm_medium = searchParams?.utm_medium || '';
  const utm_campaign = searchParams?.utm_campaign || '';

  const meta = LEAD_LABELS[leadType] || {
    title: 'Get in Touch',
    subtitle: 'We respond within 1 business day.',
    placeholder: 'Tell us about your project, requirements, or questions...',
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setMsg('');
    if (siteKey && !token) { setMsg('Please complete the verification check below.'); setState('error'); return; }
    setState('sending');
    try {
      const payload = {
        name: form.get('name'),
        email: form.get('email'),
        company: form.get('company'),
        phone: form.get('phone'),
        details: form.get('details'),
        lead_type: leadType,
        source: source || 'contact_page',
        interests: Array.from(form.getAll('interest')).map(String),
        utm_source, utm_medium, utm_campaign,
        form_ms: Date.now() - startedAt.current,
        company_url_confirm: form.get('company_url_confirm'),
        turnstileToken: token || undefined,
      };
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json().catch(() => ({} as any));
      if (res.ok && j?.ok !== false) {
        setState('sent');
        (e.target as HTMLFormElement).reset();
        setMsg("Thanks! We'll be in touch within 1 business day.");
      } else {
        setState('error');
        setMsg(j?.message || 'Something went wrong. Please try again.');
        setToken(''); setResetKey((k) => k + 1);
      }
    } catch {
      setState('error');
      setMsg('Network error. Please try again.');
    }
  }

  return (
    <section className="relative py-16 min-h-screen bg-surface-base">
      {/* Grid background */}
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(91, 149, 255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(91, 149, 255,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative max-w-6xl mx-auto px-6">

        {/* Header */}
        <div className="mb-14 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/[0.06] text-accent text-xs font-medium mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Response within 1 business day
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white/90 tracking-tight mb-4">{meta.title}</h1>
          <p className="text-zinc-400 text-lg max-w-xl mx-auto">{meta.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">

          {/* Left - contact info */}
          <div className="lg:col-span-2 space-y-6">

            {/* Contact details card */}
            <div className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-6">Contact</p>
              <div className="space-y-5">
                {CONTACT_INFO.map((item) => (
                  <a key={item.label} href={item.href} target={item.label === 'LinkedIn' || item.label === 'Office' ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="flex items-start gap-3 group">
                    <div className="w-8 h-8 rounded-lg bg-accent/8 border border-accent/15 flex items-center justify-center text-accent flex-shrink-0 mt-0.5 group-hover:bg-accent/15 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-0.5">{item.label}</p>
                      <p className="text-sm text-zinc-300 group-hover:text-accent transition-colors leading-snug">{item.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* What happens next */}
            <div className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-5">What happens next</p>
              <ol className="space-y-4">
                {[
                  ['You submit this form', 'Takes about 2 minutes.'],
                  ['We review and respond', 'Within 1 business day - usually same day.'],
                  ['15-minute intro call', 'We understand your environment and see if we are a fit.'],
                  ['Proof of concept', 'We run AutoFix or AutoScan on your actual infrastructure.'],
                ].map(([step, desc], i) => (
                  <li key={step} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center text-accent text-[10px] font-bold flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white/80">{step}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Early customers */}
            <div className="rounded-2xl border border-white/[0.06] bg-surface-1 p-7">
              <p className="text-xs font-medium text-accent uppercase tracking-widest mb-4">Trusted by</p>
              <div className="flex items-center gap-6">
                {[
                  { name: 'Petco', detail: '1,500+ stores' },
                  { name: 'ABC Fitness', detail: '500K+ members' },
                ].map((c) => (
                  <div key={c.name}>
                    <p className="text-sm font-semibold text-white/70">{c.name}</p>
                    <p className="text-[10px] text-zinc-600">{c.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - form */}
          <div className="lg:col-span-3">
            {state === 'sent' ? (
              <div className="h-full rounded-2xl border border-accent/20 bg-surface-1 p-12 flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mb-5">
                  <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-white/90 mb-2">Message sent</h2>
                <p className="text-zinc-400 mb-6">We will be in touch within 1 business day.</p>
                <Link href="/" className="text-sm text-accent hover:text-[#8fb6ff] transition-colors">
                  Back to home →
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="rounded-2xl border border-white/[0.06] bg-surface-1 p-8 space-y-4">
                {/* Hidden fields */}
                <input type="hidden" name="lead_type" value={leadType} />
                <input type="hidden" name="source" value={source || 'contact_page'} />
                <input type="hidden" name="utm_source" value={utm_source} />
                <input type="hidden" name="utm_medium" value={utm_medium} />
                <input type="hidden" name="utm_campaign" value={utm_campaign} />

                {/* Name + Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1.5 block">Full name *</label>
                    <input name="name" required placeholder="Satish Shetty"
                      className="w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1.5 block">Work email *</label>
                    <input name="email" type="email" required placeholder="you@company.com"
                      className="w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors" />
                  </div>
                </div>

                {/* Company + Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1.5 block">Company *</label>
                    <input name="company" required placeholder="Acme Corp"
                      className="w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1.5 block">Phone</label>
                    <input name="phone" type="tel" placeholder="+1 (555) 000-0000"
                      className="w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors" />
                  </div>
                </div>

                {/* Interest checkboxes */}
                <div>
                  <label className="text-xs text-zinc-500 mb-2.5 block">I am interested in</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['autofix', 'CyberArmy AutoFix'],
                      ['autoscan', 'AutoScan'],
                      ['demo', 'Product demo'],
                      ['partner', 'Partnership'],
                    ].map(([val, label]) => (
                      <label key={val} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/[0.06] bg-surface-base text-xs text-zinc-400 cursor-pointer hover:border-accent/20 hover:text-zinc-300 transition-all has-[:checked]:border-accent/30 has-[:checked]:text-accent has-[:checked]:bg-accent/[0.05]">
                        <input type="checkbox" name="interest" value={val} className="sr-only" />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="text-xs text-zinc-500 mb-1.5 block">Message *</label>
                  <textarea name="details" required rows={5} placeholder={meta.placeholder}
                    className="w-full bg-surface-base border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white/80 placeholder:text-zinc-700 focus:outline-none focus:border-accent/30 transition-colors resize-none" />
                </div>

                {/* Honeypot */}
                <input name="company_url_confirm" className="absolute opacity-0 h-0 w-0 overflow-hidden" tabIndex={-1} autoComplete="nope" aria-hidden="true" />

                {msg && (
                  <p className={`text-sm ${state === 'error' ? 'text-red-400' : 'text-zinc-400'}`}>{msg}</p>
                )}

                <Turnstile siteKey={siteKey} appearance="always" onVerify={setToken} onExpire={() => setToken('')} onError={() => setToken('')} resetKey={resetKey} />

                <button type="submit" disabled={state === 'sending' || (!!siteKey && !token)}
                  className="w-full py-3.5 rounded-xl bg-accent text-black font-bold hover:bg-accent-hover transition-all duration-300 shadow-lg shadow-accent/20 disabled:opacity-40 disabled:cursor-not-allowed text-sm">
                  {state === 'sending' ? 'Sending...' : 'Send Message'}
                </button>

                <p className="text-center text-xs text-zinc-700">
                  By submitting you agree to our{' '}
                  <Link href="/privacy" className="hover:text-zinc-500 transition-colors">Privacy Policy</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
